import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { integrationService } from '../services/api';
import { Layers, Search, Loader2, User, ArrowRight, FileText } from 'lucide-react';

const LC_TONE = {
  CONNECTED: 'bg-green-100 text-green-700 border-green-200',
  NOT_YET_CONNECTED: 'bg-amber-100 text-amber-700 border-amber-200',
  NO_CANONICAL_MODEL: 'bg-gray-100 text-gray-600 border-gray-200',
  NOT_APPLICABLE: 'bg-gray-100 text-gray-500 border-gray-200',
  INTERNAL_ONLY: 'bg-blue-100 text-blue-700 border-blue-200',
};

const SECTIONS = [
  { key: 'quotes', label: 'Quotes', empty: 'No quotes on record.',
    fields: [['service_type', 'Service'], ['status', 'Status'], ['total', 'Total', 'money'], ['revision_number', 'Rev'], ['created_at', 'Created', 'date']] },
  { key: 'jobs', label: 'Jobs / Operational History', empty: 'No jobs or loads on record.',
    fields: [['service_type', 'Service'], ['status', 'Status'], ['origin', 'Origin'], ['destination', 'Destination'], ['created_at', 'Date', 'date']] },
  { key: 'assignments', label: 'Dispatch Assignments', empty: 'Not yet connected — no dispatch assignments.',
    fields: [['driver', 'Driver'], ['status', 'Status'], ['created_at', 'Date', 'date']] },
  { key: 'completions', label: 'Job Completion / POD', empty: 'Not yet connected — no completion/POD recorded.',
    fields: [['status', 'Status'], ['completed_at', 'Completed', 'date']] },
  { key: 'invoices', label: 'Customer Invoices / AR', empty: 'Not yet connected — no customer invoice issued.',
    fields: [['status', 'Status'], ['amount', 'Amount', 'money'], ['created_at', 'Issued', 'date']] },
  { key: 'payments', label: 'Customer Payments', empty: 'Not yet connected — no customer payment recorded.',
    fields: [['method', 'Method'], ['amount', 'Amount', 'money'], ['created_at', 'Date', 'date']] },
  { key: 'documents', label: 'Documents (references only)', empty: 'No linked documents.',
    fields: [['name', 'Name'], ['type', 'Type'], ['created_at', 'Added', 'date']] },
];

const fmt = (v, kind) => {
  if (v === null || v === undefined || v === '') return '—';
  if (kind === 'money') { const n = Number(v); return Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : String(v); }
  if (kind === 'date') { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(); }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const Row = ({ row, fields }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-0.5 py-1.5 border-b border-gray-100 last:border-0">
    {fields.map(([k, label, kind]) => (
      <span key={k} className="text-xs"><span className="text-gray-400">{label}:</span> <span className="text-gray-700">{fmt(row[k], kind)}</span></span>
    ))}
  </div>
);

const Customer360Page = () => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load360 = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setError(null); setView(null);
    try { setView(await integrationService.getCustomer360(id)); }
    catch (e) { setError(e?.response?.status === 404 ? 'Customer not found' : 'Unable to load Customer 360'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const cid = params.get('customer');
    if (cid) load360(cid);
  }, [params, load360]);

  const runSearch = async () => {
    const query = q.trim();
    if (!query) { setResults([]); setNoResults(false); return; }
    setSearching(true); setNoResults(false);
    try {
      const r = await integrationService.searchCustomers(query, { limit: 10 });
      const items = r.items || [];
      setResults(items);
      setNoResults(items.length === 0);
    } catch { setResults([]); setNoResults(true); }
    finally { setSearching(false); }
  };

  const pick = (it) => { setResults([]); setQ(''); setParams({ customer: it.id }); };

  const c = view?.chain || {};

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="customer-360-page">
        <Breadcrumb items={[{ label: 'Business' }, { label: 'Customer 360' }]} />

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-lg bg-blue-100 flex items-center justify-center"><Layers className="w-6 h-6 text-blue-900" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer 360</h1>
            <p className="text-sm text-gray-500">Unified operational view of a customer relationship — lead → quotes → jobs → billing → documents.</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <label className="text-xs font-medium text-gray-500">Search by name / email / phone</label>
          <div className="flex gap-2 mt-1 relative max-w-xl">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setNoResults(false); setResults([]); }}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()} placeholder="e.g. Christie Meyers"
                data-testid="c360-search-input"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <button onClick={runSearch} data-testid="c360-search-btn"
              className="px-4 py-2 bg-blue-900 text-white rounded-lg text-sm hover:bg-blue-800 flex items-center gap-1">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
            </button>
            {results.length > 0 && (
              <div className="absolute top-11 left-0 z-10 w-full bg-white border border-gray-200 rounded-lg shadow-md max-h-60 overflow-auto" data-testid="c360-search-results">
                {results.map((it) => (
                  <button key={it.id} onClick={() => pick(it)} data-testid={`c360-result-${it.id}`}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0">
                    <span className="font-medium text-gray-800">{it.label}</span>
                    {it.snippet && <span className="text-gray-400 ml-2 text-xs">{it.snippet}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!searching && noResults && <p className="text-xs text-gray-400 mt-1" data-testid="c360-search-empty">No matching customers.</p>}
        </div>

        {loading && <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>}
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="c360-error">{error}</div>}

        {view && (
          <div className="space-y-5" data-testid="c360-result">
            {/* Identity */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"><User className="w-6 h-6 text-slate-700" /></div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900" data-testid="c360-name">
                    {view.customer.company_name || view.customer.contact_name || view.customer.full_name || view.customer.id}
                  </h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                    {view.customer.city && <span>{view.customer.city}{view.customer.state ? `, ${view.customer.state}` : ''}</span>}
                    {view.customer.status && <span>status: {view.customer.status}</span>}
                    {view.customer.provenance && <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">provenance: {view.customer.provenance}</span>}
                  </div>
                  <Link to={`/admin/leads?focus=${view.customer.id}`} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline mt-2">
                    Open in Leads <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4" data-testid="c360-counts">
                {Object.entries(view.counts || {}).map(([k, v]) => (
                  <span key={k} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                ))}
              </div>
            </div>

            {/* Lifecycle */}
            {view.lifecycle_status && (
              <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="c360-lifecycle">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Relationship Lifecycle</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(view.lifecycle_status).map(([stage, st]) => {
                    const state = (st && typeof st === 'object') ? st.state : st;
                    return (
                      <span key={stage} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${LC_TONE[state] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {stage}: {String(state || '').replace(/_/g, ' ').toLowerCase()}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Chain sections */}
            <div className="grid md:grid-cols-2 gap-5">
              {SECTIONS.map((s) => {
                const rows = c[s.key];
                return (
                  <div key={s.key} className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`c360-${s.key}`}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {s.label}</h3>
                    {Array.isArray(rows) && rows.length > 0 ? (
                      <div className="max-h-52 overflow-auto">
                        {rows.map((r, i) => <Row key={i} row={r} fields={s.fields} />)}
                      </div>
                    ) : <p className="text-xs text-gray-400">{s.empty}</p>}
                  </div>
                );
              })}
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="c360-timeline">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Activity Timeline</h3>
              {Array.isArray(view.operations_timeline) && view.operations_timeline.length > 0 ? (
                <div className="space-y-1 max-h-60 overflow-auto">
                  {view.operations_timeline.map((r, i) => {
                    const when = r.date || r.created_at || r.timestamp || r.effective_date;
                    const what = r.event || r.action || r.type || r.stage || r.status;
                    const detail = r.summary || r.detail || r.description || r.scope_summary;
                    return (
                      <div key={i} className="flex items-start gap-2 py-1 border-b border-gray-100 last:border-0 text-xs">
                        <span className="text-gray-400 whitespace-nowrap">{when ? fmt(when, 'date') : '—'}</span>
                        <span className="text-gray-800 font-medium">{what || 'event'}</span>
                        {detail && <span className="text-gray-500 truncate">— {String(detail)}</span>}
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-xs text-gray-400">No recorded activity yet.</p>}
            </div>

            <p className="text-xs text-gray-400">
              Technical / registry inspection lives in the read-only{' '}
              <Link to={`/admin/integration?customer=${view.customer.id}`} className="text-blue-600 hover:underline">Integration Console</Link>.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Customer360Page;
